const INVALID_COMPANY_DOMAINS_ERROR =
  'Enter 1-20 valid domains separated by commas.';
const MAX_COMPANY_DOMAINS = 20;
const DOMAIN_LABEL = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/u;

export type CompanyDomainsParseResult =
  | { domains: string[]; success: true }
  | { error: string; success: false };

export type ChangedCompanyDomainsResult =
  | { domains?: string[]; success: true }
  | { error: string; success: false };

const invalidResult = (): CompanyDomainsParseResult => ({
  error: INVALID_COMPANY_DOMAINS_ERROR,
  success: false,
});

const containsOnlyPrintableAscii = (input: string): boolean =>
  Array.from(input).every((character) => {
    const codePoint = character.codePointAt(0);
    return codePoint !== undefined && codePoint >= 0x20 && codePoint <= 0x7e;
  });

const isValidDomain = (domain: string): boolean => {
  if (domain.length < 1 || domain.length > 253) return false;

  const labels = domain.split('.');
  const finalLabel = labels.at(-1);

  return (
    labels.length >= 2 &&
    finalLabel !== undefined &&
    !/^\d+$/u.test(finalLabel) &&
    labels.every((label) => DOMAIN_LABEL.test(label))
  );
};

const normalizeCompanyDomain = (domain: string): string =>
  domain.replace(/^ +| +$/gu, '').toLowerCase();

export const parseCompanyDomains = (
  input: unknown,
): CompanyDomainsParseResult => {
  if (typeof input !== 'string' || !containsOnlyPrintableAscii(input)) {
    return invalidResult();
  }

  const entries = input.split(',');
  if (entries.length < 1 || entries.length > MAX_COMPANY_DOMAINS) {
    return invalidResult();
  }

  const domains = entries.map(normalizeCompanyDomain);
  if (!domains.every(isValidDomain)) return invalidResult();

  return {
    domains: [...new Set(domains)].sort(),
    success: true,
  };
};

export const deriveChangedCompanyDomains = (
  input: unknown,
  currentDomains: string[],
): ChangedCompanyDomainsResult => {
  const initialInput = currentDomains.join(',');
  const parsedCurrentDomains = parseCompanyDomains(initialInput);

  if (
    !parsedCurrentDomains.success &&
    typeof input === 'string' &&
    input === initialInput
  ) {
    return { success: true };
  }

  const parsed = parseCompanyDomains(input);
  if (!parsed.success) return parsed;

  if (!parsedCurrentDomains.success) {
    return { domains: parsed.domains, success: true };
  }

  const domainsChanged =
    parsed.domains.length !== parsedCurrentDomains.domains.length ||
    parsed.domains.some(
      (domain, index) => domain !== parsedCurrentDomains.domains[index],
    );

  return domainsChanged
    ? { domains: parsed.domains, success: true }
    : { success: true };
};
